import { useCallback, useState } from 'react'

import { useAppAlertsActionsContext } from '../../AppAlerts/AppAlertsProvider'
import {
  SERVICES_NAMES,
  SHIPPING_STATUS_CODES,
} from '../../../common/constants'
import {
  getIsDownloadPODAllowed,
  downloadShippingLabel,
} from '../../../context/business'
import { usePrinter } from '../../../hooks/usePrinter'
import useTranslations from '../../../i18n/useTranslations'
import {
  useShippingsLabels,
  useFetchShippingLabels,
} from '../../../services/shippingsLabels'
import { useShippingCancelMutation } from '../../../services/shippingsTracking'
import {
  MODEL_TYPE_NAMES,
  PRINT_POSITION_TYPES,
} from '../../../AppProviders/PrinterProvider'
import { negate } from 'lodash'
import TempaXLSX from 'xlsx'
import { TRACKING_OPTIONS } from '../TrackingTypeSwitcher'
import { useFetchPOD } from '../../../services/fetchPOD'
import { format } from 'date-fns'

const canBeCancelled = shippingInfo =>
  !shippingInfo.isCancelled &&
  shippingInfo.shippingStatusCode.code === SHIPPING_STATUS_CODES.manifested

const canBePrinted = shippingInfo =>
  shippingInfo.shippingStatusCode.canPrint &&
  shippingInfo.shippingStatusCode.code === SHIPPING_STATUS_CODES.manifested &&
  shippingInfo.isCancelled === false &&
  !shippingInfo.services.some(
    additional => additional.additional_code === 'ERE',
  )

export const canBeReturned = shippingInfo => {
  const validDestinCountryCode = 'ES'
  //DESCOMENTAR CUANDO SE ACTIVEN LAS DEVOLUCIONES
  const validStatusCodes = [
    /*SHIPPING_STATUS_CODES.collected,
    SHIPPING_STATUS_CODES.inTransit,
    SHIPPING_STATUS_CODES.delivering,
    SHIPPING_STATUS_CODES.incidents,*/
    SHIPPING_STATUS_CODES.delivered,
  ]
  return (
    shippingInfo.destinCountryCode === validDestinCountryCode &&
    validStatusCodes.includes(shippingInfo.shippingStatusCode.code) &&
    !shippingInfo.relatedReturn?.reverseReturnShippingCode &&
    !shippingInfo.services?.some(
      additional => additional.additional_code === 'PER',
    )
  )
}

export const isUnscheduledPickup = shippingInfo =>
  shippingInfo.pickupUnscheduled

const useShippingActions = () => {
  const t = useTranslations()

  const {
    isWorking: isPrinterServiceWorking,
    printer,
    modelType,
    printData,
    printPosition,
  } = usePrinter()

  const {
    setShowPrintAppNotInstalledAlert,
    setShowPrinterNotConfiguredAlert,
    setShowCancelShippingsAlert,
    setShowCancelPrintAlert,
  } = useAppAlertsActionsContext()

  const shouldAskPrintPosition =
    (modelType === MODEL_TYPE_NAMES.multi3 ||
      modelType === MODEL_TYPE_NAMES.multi4) &&
    printPosition === PRINT_POSITION_TYPES.askAlways

  const [shippingsToDownload, setShippingsToDownload] = useState([])
  useShippingsLabels(
    shippingsToDownload.map(shippingInfo => shippingInfo.shippingCode),
    modelType,
    // NOTE: a día de hoy, la descarga siempre se solicita desde la misma
    // posición, independientemente de la configuración que tenga el usuario.
    0,
    labelData => {
      downloadShippingLabel(labelData)
      setShippingsToDownload([])
    },
  )
  const { fetchPOD } = useFetchPOD()
  const [shippingsToPrint, setShippingsToPrint] = useState([])
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  useShippingsLabels(
    shippingsToPrint.map(shippingInfo => shippingInfo.shippingCode),
    modelType,
    modelType !== MODEL_TYPE_NAMES.multi3 &&
      modelType !== MODEL_TYPE_NAMES.multi4
      ? 0
      : printPosition,
    labelData => {
      setShowSuccessModal(true)
      printData(labelData)
      setShippingsToPrint([])
    },
  )

  const shippingMutation = useShippingCancelMutation({
    useErrorBoundary: false,
  })

  // FIXME No hay ningún motivo por el cual una etiqueta deba ser preservada en
  // caché o refrescada con React Query. Esta acción debería disparar la
  // petición de la etiqueta y actuar sobre ella, en lugar de delegarlo
  // indirectamente al onSuccess de la query, como ahora mismo ocurre.
  const downloadShippings = useCallback(
    async shippingsInfo => {
      if (shippingsInfo.some(negate(canBePrinted))) {
        setShowCancelPrintAlert(true)
        return false
      } else {
        setShippingsToDownload(shippingsInfo)
      }
    },
    [setShowCancelPrintAlert],
  )

  // FIXME No hay ningún motivo por el cual una etiqueta deba ser preservada en
  // caché o refrescada con React Query. Esta acción debería disparar la
  // petición de la etiqueta y actuar sobre ella, en lugar de delegarlo
  // indirectamente al onSuccess de la query, como ahora mismo ocurre.
  const printShippings = useCallback(
    async shippingsInfo => {
      if (!isPrinterServiceWorking) {
        setShowPrintAppNotInstalledAlert(true)
        return false
      }

      if (printer === '') {
        setShowPrinterNotConfiguredAlert(true)
        return false
      }

      if (shippingsInfo.some(negate(canBePrinted))) {
        setShowCancelPrintAlert(true)
        return false
      }

      if (shouldAskPrintPosition) {
        setShippingsToPrintAfterUserConfirm(shippingsInfo)
        return false
      }
      setShippingsToPrint(shippingsInfo)
      return true
    },
    [
      isPrinterServiceWorking,
      printer,
      shouldAskPrintPosition,
      setShowPrintAppNotInstalledAlert,
      setShowPrinterNotConfiguredAlert,
      setShowCancelPrintAlert,
    ],
  )

  const generateExportContactsData = useCallback(
    contactsInfo => {
      const columns = [
        { title: t('noun:contact-client-code'), width: { wch: 160 / 7 } },
        { title: t('noun:name'), width: { wch: 250 / 7 } },
        { title: t('noun:phone'), width: { wch: 100 / 7 } },
        { title: t('noun:email'), width: { wch: 130 / 7 } },
        { title: t('noun:address'), width: { wch: 150 / 7 } },
        { title: t('noun:city'), width: { wch: 100 / 7 } },
        { title: t('noun:postal-code'), width: { wch: 120 / 7 } },
        { title: t('noun:country'), width: { wch: 120 / 7 } },
      ]
      const rows = contactsInfo.map(contactInfo => [
        contactInfo?.contactClientCode,
        contactInfo?.name,
        contactInfo?.phone,
        contactInfo?.email,
        contactInfo?.address,
        contactInfo?.city,
        contactInfo?.postalCode,
        contactInfo?.country,
      ])

      return { columns, rows }
    },
    [t],
  )

  const exportContactsToExcel = useCallback(
    contactsInfo => {
      const { columns, rows } = generateExportContactsData(contactsInfo)
      const headerRow = columns.map(column => column.title)
      const dataWithHeader = [headerRow, ...rows]

      const columnsWidth = columns.map(column => column.width)

      const ws = TempaXLSX.utils.aoa_to_sheet(dataWithHeader, { header: 1 })
      ws['!cols'] = columnsWidth

      const wb = TempaXLSX.utils.book_new()
      const title = t('noun:contacts')
      TempaXLSX.utils.book_append_sheet(wb, ws, title)

      TempaXLSX.writeFile(wb, title + '.xlsx')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [generateExportContactsData],
  )

  const generateExportData = useCallback(
    shippingsInfo => {
      const columns = [
        { title: t('noun:shippingCode'), width: { wch: 160 / 7 } },
        { title: t('noun:shippingDate'), width: { wch: 110 / 7 } },
        { title: t('noun:shippingTypeCode'), width: { wch: 100 / 7 } },
        { title: t('noun:total-packages'), width: { wch: 50 / 7 } },
        { title: t('noun:shippingStatusCode'), width: { wch: 160 / 7 } },
        { title: t('noun:incidentDescription'), width: { wch: 160 / 7 } },
        { title: t('noun:clientCenterCode'), width: { wch: 100 / 7 } },
        { title: t('noun:clientReference'), width: { wch: 100 / 7 } },
        { title: t('noun:CP-sender'), width: { wch: 100 / 7 } },
        { title: t('noun:senderAddress2'), width: { wch: 120 / 7 } },
        { title: t('noun:CP-destination'), width: { wch: 100 / 7 } },
        { title: t('noun:recipientAddress2'), width: { wch: 120 / 7 } },
        { title: t('noun:sender'), width: { wch: 130 / 7 } },
        { title: t('noun:recipientName'), width: { wch: 130 / 7 } },

        { title: t('noun:CTT-point'), width: { wch: 130 / 7 } },

        { title: t('noun:deliveryDate'), width: { wch: 100 / 7 } },
        { title: t('noun:refund-amount'), width: { wch: 160 / 7 } },

        { title: t('noun:albaran-pt'), width: { wch: 100 / 7 } },
        { title: t('noun:return'), width: { wch: 160 / 7 } },
        { title: t('noun:declared-price'), width: { wch: 100 / 7 } },
        { title: t('noun:scaned-albaran'), width: { wch: 130 / 7 } },
        { title: t('noun:dni-verification'), width: { wch: 100 / 7 } },
        { title: t('noun:reference2'), width: { wch: 100 / 7 } },
      ]
      const rows = shippingsInfo.map(shippingInfo => [
        shippingInfo.shippingCode,
        shippingInfo?.manifestDate
          ? format(new Date(shippingInfo?.manifestDate), 'dd/MM/yyyy HH:mm')
          : shippingInfo.manifestedDate,
        SERVICES_NAMES.hasOwnProperty(shippingInfo.bestShippingTypeCode)
          ? SERVICES_NAMES[shippingInfo.bestShippingTypeCode]
          : shippingInfo.bestShippingTypeCode,
        shippingInfo.itemCount,
        shippingInfo.state.incidentCode !== (null || undefined || false)
          ? formatName(shippingInfo.state.lastStatusDescription)
          : shippingInfo.state.lastStatusDescription,
        shippingInfo.incident.description,
        shippingInfo.clientCenterCode,
        shippingInfo.clientReference,
        shippingInfo.senderPostalCode,
        shippingInfo.senderAddress2,
        shippingInfo.destinPostalCode,
        shippingInfo.recipientAddress2,
        shippingInfo.senderName,
        shippingInfo.recipientName,
        shippingInfo?.services?.find(
          ({ additional_code, additional_flag }) =>
            additional_code === 'PER' ||
            additional_code === 'CBX' ||
            additional_code === 'RLI' ||
            additional_code === 'RIP' ||
            additional_code === 'RDP',
        )
          ? t('noun:yes')
          : t('noun:no'),
        shippingInfo.committedDeliveryDatetime,
        shippingInfo.refundValue,
        shippingInfo.cttPrimeShippingCode,
        shippingInfo?.services?.find(
          ({ additional_code, additional_flag }) =>
            additional_code === 'RET' && additional_flag,
        )
          ? shippingInfo.relatedReturn?.returnOnDeliveryShippingCode ??
            t('noun:yes')
          : t('noun:no'),
        shippingInfo?.services?.find(
          ({ additional_code, additional_flag }) => additional_code === 'VDE',
        )?.additional_value ?? '',
        shippingInfo?.services?.find(
          ({ additional_code, additional_flag }) =>
            additional_code === 'EAC' && additional_flag,
        )
          ? t('noun:yes')
          : t('noun:no'),
        shippingInfo?.services?.find(
          ({ additional_code, additional_flag }) =>
            additional_code === 'RDI' || additional_code === 'IDE',
        )
          ? t('noun:yes')
          : t('noun:no'),
        shippingInfo.clientReference2,
      ])

      return { columns, rows }
    },
    [t],
  )

  const formatName = name => {
    if (name !== '-') {
      switch (name.toLowerCase()) {
        case 'recogido':
          return `Incidencia en la recogida`
        case 'entregado':
          return `Incidencia en la entrega`
        case 'devuelto':
          return `Incidencia en la devolución`
        default:
          return `Incidencia en el ${name.toLowerCase()}`
      }
    }
  }

  const exportToExcel = useCallback(
    shippingsInfo => {
      const { columns, rows } = generateExportData(shippingsInfo)
      const headerRow = columns.map(column => column.title)
      const dataWithHeader = [headerRow, ...rows]

      const columnsWidth = columns.map(column => column.width)

      const ws = TempaXLSX.utils.aoa_to_sheet(dataWithHeader, { header: 1 })
      ws['!cols'] = columnsWidth

      const wb = TempaXLSX.utils.book_new()
      const title = t('noun:shipments')
      TempaXLSX.utils.book_append_sheet(wb, ws, title)

      TempaXLSX.writeFile(wb, title + '.xlsx')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [generateExportData],
  )

  const [shippingsInfoToCancel, setShippingsInfoToCancel] = useState([])
  const beginShippingCancellation = useCallback(
    async shippingsInfoToBeCancelled => {
      const canShippingsBeCancelled =
        shippingsInfoToBeCancelled.every(canBeCancelled)

      if (canShippingsBeCancelled) {
        setShippingsInfoToCancel(shippingsInfoToBeCancelled)
      } else {
        setShowCancelShippingsAlert(true)
      }
    },
    [setShowCancelShippingsAlert],
  )
  const confirmShippingsCancellation = async () =>
    Promise.all(
      shippingsInfoToCancel.map(shippingInfo =>
        shippingMutation.mutateAsync(shippingInfo.shippingCode),
      ),
    ).then(() => {
      setShippingsInfoToCancel([])
    })
  const abortShippingsCancellation = () => {
    setShippingsInfoToCancel([])
  }

  const [
    shippingsToPrintAfterUserConfirm,
    setShippingsToPrintAfterUserConfirm,
  ] = useState([])

  const abortPrintProcess = () => {
    setShippingsToPrintAfterUserConfirm([])
  }

  const fetchShippingLabels = useFetchShippingLabels()
  const confirmPrintShippings = position => {
    fetchShippingLabels(
      shippingsToPrintAfterUserConfirm.map(
        shippingInfo => shippingInfo.shippingCode,
      ),
      modelType,
      position,
    ).then(labelData => {
      printData(labelData)
      setShippingsToPrintAfterUserConfirm([])
    })
  }

  // activate returns helpers

  const [activateReturnShippingInfo, setActivateReturnShippingInfo] = useState()

  const cancelActivateReturn = () => setActivateReturnShippingInfo()

  // scheduled pickup helpers

  const [scheduledPickupShippingsInfo, setScheduledPickupShippingsInfo] =
    useState([])

  const cancelScheduledPickup = () => setScheduledPickupShippingsInfo([])

  // shipping actions helpers

  const determineShippingActions = useCallback(
    (shippingInfo, shippingsType) => {
      return {
        printLabel: {
          label: t('action:print-shipping-label', {
            count: 1,
          }),
          onClick: () => {
            printShippings([shippingInfo])
          },
          isActive: canBePrinted(shippingInfo),
        },
        downloadLabel: {
          label: t('action:download-shipping-label', {
            count: 1,
          }),
          onClick: () => downloadShippings([shippingInfo]),
          isActive: canBePrinted(shippingInfo),
        },
        downloadPOD: {
          label: t('action:download-pod'),
          onClick: () => fetchPOD(shippingInfo),
          isActive: getIsDownloadPODAllowed(
            shippingInfo.shippingStatusCode.code,
          ),
        },
        cancelShipping: {
          label: t('action:cancel-shipment', {
            count: 1,
          }),
          onClick: () => beginShippingCancellation([shippingInfo]),
          isActive: canBeCancelled(shippingInfo),
        },
        activateReturn: {
          label: t('action:activate-return'),
          onClick: () => setActivateReturnShippingInfo(shippingInfo),
          isActive:
            canBeReturned(shippingInfo) &&
            shippingsType !== TRACKING_OPTIONS.unscheduledPickups,
        },
        unscheduledPickup: {
          label: t('action:schedule-pickup'),
          onClick: () => setScheduledPickupShippingsInfo([shippingInfo]),
          isActive: isUnscheduledPickup(shippingInfo),
        },
      }
    },
    [beginShippingCancellation, downloadShippings, printShippings, t],
  )

  const determineShippingActionsMobile = useCallback(
    shippingInfo => ({
      downloadLabel: {
        label: t('action:download-shipping-label', {
          count: 1,
        }),
        onClick: () => downloadShippings([shippingInfo]),
        isActive: canBePrinted(shippingInfo),
      },
    }),
    [downloadShippings, t],
  )

  const determineShippingsGroupOptions = useCallback(
    (shippingsInfo, successCallback, shippingsType) => {
      return [
        {
          text: t('action:schedule-pickup-option', {
            count: shippingsInfo.length,
          }),
          onClick: () => setScheduledPickupShippingsInfo(shippingsInfo),
          isActive:
            shippingsInfo.length &&
            shippingsType === TRACKING_OPTIONS.unscheduledPickups,
          count: shippingsInfo.length,
        },
        {
          text: t('action:download-shipping-label', {
            count: shippingsInfo.length,
          }),
          onClick: () => downloadShippings(shippingsInfo),
          isActive: shippingsInfo.some(canBePrinted),
        },
        {
          text: t('action:print-shipping-label', {
            count: shippingsInfo.length,
          }),
          onClick: () =>
            printShippings(shippingsInfo).then(success => {
              if (!success) return
              successCallback()
            }),
          isActive: shippingsInfo.some(canBePrinted),
        },
        {
          text: t('action:cancel-shipment', {
            count: shippingsInfo.length,
          }),
          onClick: () =>
            beginShippingCancellation(shippingsInfo).then(successCallback),
          isActive: shippingsInfo.some(canBeCancelled),
        },
        {
          text: t('action:export-excel', {
            count: shippingsInfo.length,
          }),
          onClick: () => exportToExcel(shippingsInfo),
          isActive: true,
        },
      ]
    },
    [
      beginShippingCancellation,
      downloadShippings,
      printShippings,
      t,
      exportToExcel,
    ],
  )

  return {
    determineShippingActions,
    determineShippingActionsMobile,
    determineShippingsGroupOptions,
    shippingsInfoToCancel,
    confirmShippingsCancellation,
    abortShippingsCancellation,
    confirmPrintShippings,
    shippingsToPrintAfterUserConfirm,
    abortPrintProcess,
    activateReturnShippingInfo,
    cancelActivateReturn,
    scheduledPickupShippingsInfo,
    cancelScheduledPickup,
    setShowSuccessModal,
    showSuccessModal,
    exportContactsToExcel,
  }
}

export default useShippingActions
